import { ApiPagination, useApiPaginationUrlSync } from "./useApiPaginationUrlSync";
import { useSearchParams } from "react-router";
import { useApiSortUrlSync } from "./useApiSortUrlSync";
import { ApiFiltersUrlSyncOptions, useApiFiltersUrlSync } from "./useApiFiltersUrlSync";
import { useEffect, useMemo, useRef } from "react";

export type ApiParams = {
    filters: AxiosParams;
    sort: AxiosParams;
    pagination: ApiPagination;
    allAsAxiosParams: AxiosParams;
};

// The DEFAULTS is handled here, but I would suspect this DEFAULT reset would need to
// be in ANOTHER special component because it shouldn't relate to EITHER `urlToApi` or even `urlToMrt`.
type DefaultUrlParams = ApiPagination & Partial<Record<string, string>> & Pick<ApiFiltersUrlSyncOptions, "tagContext">;
export const useUrlToApi = (options?: DefaultUrlParams) => {
    // Since the URL is the Entrypoint of what's computed here
    // We shouldn't pass a hardcoded Init value.
    const [searchParams, _setSearchParams] = useSearchParams();

    const memoizedDefaults = useMemo(() => {
        return options || {};
    }, []);

    // Track if initial cleanup has been done to avoid re-running
    const hasInitialCleanupRef = useRef(false);

    useEffect(() => {
        // Make sure the defaults entered at init, are UNSET.
        // This should only run once on initial load
        if (hasInitialCleanupRef.current) {
            return;
        }

        const newSearch = new URLSearchParams(searchParams);
        let hasChanged = false;

        Object.entries(memoizedDefaults).forEach(([k, v]) => {
            const previousVal = searchParams.get(k);
            if (previousVal === v) {
                newSearch.delete(k);
                hasChanged = true;
            }
        });

        if (hasChanged) {
            _setSearchParams(newSearch);
        }

        hasInitialCleanupRef.current = true;
    }, [searchParams, memoizedDefaults, _setSearchParams]);

    // Those functions shouldn't be HOOKS
    // So that I can test them properly
    const { pagination } = useApiPaginationUrlSync({
        searchParams,
        paginationDefaults: {
            page: memoizedDefaults.page,
            itemsPerPage: memoizedDefaults.itemsPerPage,
        },
    });
    const { sort } = useApiSortUrlSync({ searchParams });
    const { filters } = useApiFiltersUrlSync({ searchParams, tagContext: memoizedDefaults.tagContext });

    const allParams = useMemo(() => {
        return {
            ...filters,
            ...sort,
            ...pagination,
        };
    }, [filters, sort, pagination, searchParams]);

    return {
        filters,
        sort,
        pagination,
        allAsAxiosParams: allParams,
    };
};
